import { useCallback, useEffect, useRef, useState } from "react";
import useToken from "app/hoc/bidder/useToken";
import { useDispatch } from "react-redux";
import {
  setBidderTokens,
  setBidderUser,
  setBidderOnboarding,
  setOnboarding,
  clearBidderAuth,
  clearAuth,
  clearGuestUser,
} from "store";
import Router from "next/router";
import { getBidderOnboardingOfUser } from "services";
import { DateTime } from "luxon";

const useAuthV2 = () => {
  const dispatch = useDispatch();
  const { token, isLoading } = useToken();

  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [isOnVerifyPage, setIsOnVerifyPage] = useState(false);

  const isNavigatingRef = useRef(false);
  const periodicCheckRef = useRef<NodeJS.Timeout | null>(null);

  const getISTDate = useCallback(() => {
    return DateTime.now().setZone("Asia/Kolkata").toFormat("yyyy-MM-dd");
  }, []);

  const is2FAVerifiedForToday = useCallback(
    (verified2faDate: any) => {
      return verified2faDate === getISTDate();
    },
    [getISTDate]
  );

  const safeReplace = useCallback((url: string) => {
    if (isNavigatingRef.current) return;
    isNavigatingRef.current = true;
    Router.replace(url);
    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 1000);
  }, []);

  const check2faVerification = useCallback(async () => {
    // console.log("checking 2fa verification");
    const res = await fetch("/api/2fa", { method: "GET" });
    if (!res.ok) {
      console.error("2FA check failed:", res.status);
      return false;
    }
    const data = await res.json();
    const verified = is2FAVerifiedForToday(data.date);
    return verified;
  }, [is2FAVerifiedForToday]);

  const stopPeriodicCheck = useCallback(() => {
    if (periodicCheckRef.current) {
      clearInterval(periodicCheckRef.current);
      periodicCheckRef.current = null;
    }
  }, []);

  // const startPeriodicCheck = useCallback(() => {
  //   if (periodicCheckRef.current) {
  //     clearInterval(periodicCheckRef.current);
  //     periodicCheckRef.current = null;
  //   }

  //   periodicCheckRef.current = setInterval(async () => {
  //     const currentPathInInterval = window.location.pathname;

  //     if (
  //       currentPathInInterval === "/login" ||
  //       currentPathInInterval === "/verify"
  //     ) {
  //       return;
  //     }

  //     const verified = await check2faVerification();
  //     console.log("verified: ", verified);
  //     if (!verified) {
  //       console.log(
  //         "🚨 Periodic check: 2FA not verified, redirecting to verify page"
  //       );
  //       setIsOnVerifyPage(true);
  //       safeReplace("/verify");
  //     }
  //   }, 30000);
  // }, [check2faVerification, safeReplace]);

  const logoutAndRedirect = useCallback(
    async (redirectTo: string = "/login") => {
      stopPeriodicCheck();
      try {
        await fetch("/api/logout", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
      } catch (e) {}
      dispatch(clearBidderAuth());
      dispatch(clearAuth());
      dispatch(clearGuestUser());
      setAuthCheckComplete(true);
      setIsOnVerifyPage(false);
      safeReplace(redirectTo);
    },
    [dispatch, safeReplace, stopPeriodicCheck]
  );

  useEffect(() => {
    (async () => {
      if (token && token.access_token) {
        if (token?.login_date) {
          const todayIST = DateTime.now().setZone("Asia/Kolkata");
          const loginDate = DateTime.fromFormat(
            token?.login_date,
            "yyyy-MM-dd",
            {
              zone: "Asia/Kolkata",
            }
          );
          const daysPassed = todayIST.diff(loginDate, "days").days;
          console.log("daysPassed: ", daysPassed);
          if (daysPassed > 30) {
            console.log("Session expired (30+ days). Logging out...");
            await logoutAndRedirect("/login?sessionExpired=true");
            return;
          }
          dispatch(setBidderTokens(token));
          dispatch(setBidderUser(token.data));
          const onboardingData: any = await getBidderOnboardingOfUser(
            token?.data?.uuid
          );
          dispatch(setOnboarding(onboardingData));
          dispatch(setBidderOnboarding(onboardingData));
          setAuthCheckComplete(true);

          if (typeof window !== "undefined") {
            const currentPath = window.location.pathname;
            if (currentPath !== "/login") {
              const verified = await check2faVerification();
              if (!verified) {
                console.log(
                  "2FA not verified on login, redirecting to verify page"
                );
                setIsOnVerifyPage(true);
                safeReplace("/verify");
                return;
              }
            }
          }
        } else {
          await logoutAndRedirect();
          dispatch(clearGuestUser());
          return;
        }
      }
      if (token && !token.isLoggedIn) {
        await logoutAndRedirect();
      }
    })();
  }, [token, logoutAndRedirect, check2faVerification, dispatch, safeReplace]);

  // Listen for route changes to stop/start periodic checking
  // useEffect(() => {
  //   if (typeof window === "undefined") return;

  //   if (!token?.isLoggedIn) {
  //     stopPeriodicCheck();
  //     return;
  //   }

  //   const handleRouteChange = () => {
  //     const currentPath = window.location.pathname;
  //     console.log("📍 Route changed to:", currentPath);

  //     if (currentPath === "/login" || currentPath === "/verify") {
  //       console.log("🛑 Stopping periodic check - on login/verify page");
  //       stopPeriodicCheck();
  //     } else {
  //       console.log("🚀 Starting periodic check - on protected page");
  //       startPeriodicCheck();

  //       setTimeout(async () => {
  //         const currentPathInInterval = window.location.pathname;
  //         if (
  //           currentPathInInterval !== "/login" &&
  //           currentPathInInterval !== "/verify"
  //         ) {
  //           const verified = await check2faVerification();
  //           if (!verified) {
  //             console.log(
  //               "🚨 Immediate check: 2FA not verified, redirecting to verify page"
  //             );
  //             setIsOnVerifyPage(true);
  //             safeReplace("/verify");
  //           }
  //         }
  //       }, 100);
  //     }
  //   };

  //   Router.events.on("routeChangeComplete", handleRouteChange);

  //   handleRouteChange();

  //   return () => {
  //     Router.events.off("routeChangeComplete", handleRouteChange);
  //     stopPeriodicCheck();
  //   };
  // }, [
  //   token?.isLoggedIn,
  //   check2faVerification,
  //   safeReplace,
  //   startPeriodicCheck,
  //   stopPeriodicCheck,
  // ]);

  // useEffect(() => {
  //   return () => {
  //     stopPeriodicCheck();
  //     isNavigatingRef.current = false;
  //   };
  // }, [stopPeriodicCheck]);

  return {
    token,
    authCheckComplete,
  };
};

export default useAuthV2;
